import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Divider, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import { motion } from 'framer-motion'; // You'll need to install this package

// Styled components for enhanced visuals
const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  backgroundColor: '#f5f5f5',
  color: '#BF0A30',
  marginBottom: theme.spacing(2),
  border: '3px solid #BF0A30',
  boxShadow: '0 8px 16px rgba(191, 10, 48, 0.15)',
}));

const StyledCard = styled(motion.div)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  overflow: 'hidden',
  background: 'white',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
}));

const CardInner = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '8px',
    background: 'linear-gradient(90deg, #BF0A30, #FF4D6D)',
  },
}));

const HeaderDivider = styled(Divider)(({ theme }) => ({
  width: 120,
  height: 4,
  margin: '0 auto 2rem auto',
  background: 'linear-gradient(90deg, #BF0A30, #FF4D6D)',
  borderRadius: 2,
}));

const BackgroundDecoration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '300px',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #e2e8f0 100%)',
  clipPath: 'polygon(0 0, 100% 0, 100% 75%, 0 100%)',
  zIndex: -1,
}));

const KeyManagerialPersonnel = () => {
  const personnelData = [
    {
      id: 1,
      name: "Sudhakar Reddy",
      position: "Managing Director (MD)",
      company: "Shilpaa Electrical Infra Tech India Pvt Ltd",
      description: "Sudhakar Reddy brings over 35 years of domain expertise in electrical infrastructure, EPC project execution, and large-scale utility systems. As Managing Director, he is responsible for end-to-end operational oversight, technical strategy formulation, and compliance with industry standards and regulatory frameworks.\n\nWith extensive experience in managing high-voltage transmission projects, substation development, and industrial electrification, Sudhakar ensures that all projects are executed with precision, cost-efficiency, and adherence to safety protocols. His leadership has been instrumental in driving the company's growth in mission-critical infrastructure development and delivering technically robust solutions across sectors including power distribution, renewable integration, and industrial automation.",
      // You can add actual image paths when available
      imagePath: null
    },
    {
      id: 2,
      name: "Sai Nagi Reddy Gurijala",
      position: "Chief Executive Officer (CEO)",
      company: "Shilpaa Electrical Infra Tech India Pvt Ltd",
      description: "Sai Nagi Reddy Gurijala is the Chief Executive Officer of Shilpaa Electrical Infra Tech (India) Pvt Ltd, where he leads strategic planning, project execution, and business development. A graduate in Computational Mathematics from Mahindra University, he brings analytical precision and a data-driven approach to decision-making. His leadership focuses on innovation, digital transformation, and operational efficiency, driving the company’s expansion in the high-voltage EPC (Engineering, Procurement & Construction) sector. Under his guidance, Shilpaa is broadening its geographic footprint and strengthening its core infrastructure capabilities. Sai Nagi Reddy is also the Co-Founder of PicklePlex, where he leads strategy and operations, reflecting his entrepreneurial mindset and commitment to excellence across both industrial and consumer-facing ventures.",
      imagePath: null
    },
    {
      id: 3,
      name: "D.V.S. Subrahmanyam",
      position: "Chief Financial Officer (CFO)",
      company: "Shilpaa Electrical Infra Tech India Pvt Ltd",
      description: "Mr. D.V.S. Subrahmanyam is a seasoned finance professional with over 25 years of experience in corporate finance, strategic planning, and financial governance. As the Chief Financial Officer of Shilpaa Electrical Infra Tech India Pvt Ltd, he oversees the company's financial health, risk management, budgeting, and compliance functions.\n\nHolding a Master's degree in Commerce, Mr. Subrahmanyam brings deep expertise in accounting principles, cost control systems, and statutory compliance. He plays a crucial role in aligning financial strategy with business goals, ensuring efficient capital allocation, and maintaining robust fiscal discipline across all operational verticals. His analytical acumen and financial stewardship continue to strengthen the company's performance and long-term sustainability.",
      imagePath: null
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    },
    hover: { 
      y: -15,
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)'
    }
  };

  return (
    <Box sx={{
      py: 10,
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(to bottom, #f8fafc, #ffffff)'
    }}>
      <BackgroundDecoration />
      
      <Container maxWidth="lg">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 800, 
                color: '#1a2a41',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -5,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  background: '#BF0A30',
                  borderRadius: '2px',
                }
              }}
            >
              Leadership Team
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mt: 4, 
                fontWeight: 400,
                lineHeight: 1.8
              }}
            >
              Meet the accomplished professionals driving our vision and operational excellence at Shilpaa Electrical Infra Tech India.
            </Typography>
            <HeaderDivider />
          </Box>
        </motion.div>

        {/* Personnel Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={5}>
            {personnelData.map(person => (
              <Grid item xs={12} md={6} lg={4} key={person.id}>
                <StyledCard
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <CardInner>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                      <ProfileAvatar src={person.imagePath}>
                        {!person.imagePath && (
                          <PersonIcon fontSize="large" />
                        )}
                      </ProfileAvatar>
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#1a2a41', 
                          textAlign: 'center',
                          mt: 2
                        }}
                      >
                        {person.name}
                      </Typography>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#BF0A30', 
                          fontWeight: 600, 
                          textAlign: 'center',
                          letterSpacing: 0.5,
                          mb: 1
                        }}
                      >
                        {person.position}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary', 
                          fontStyle: 'italic', 
                          textAlign: 'center', 
                          mb: 3,
                          fontSize: '0.85rem'
                        }}
                      >
                        {person.company}
                      </Typography>
                      <Divider sx={{ width: 40, height: 3, backgroundColor: '#BF0A30', borderRadius: 2 }} />
                    </Box>
                    
                    <Box sx={{ mt: 3, flexGrow: 1 }}>
                      {person.description.split('\n\n').map((paragraph, index) => (
                        <Typography 
                          key={index} 
                          variant="body2" 
                          paragraph 
                          sx={{ 
                            color: 'text.secondary', 
                            lineHeight: 1.8,
                            textAlign: 'justify',
                            fontSize: '0.95rem'
                          }}
                        >
                          {paragraph}
                        </Typography>
                      ))}
                    </Box>
                  </CardInner>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default KeyManagerialPersonnel;